import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Accordion from '@radix-ui/react-accordion';
import { primaryColors, secondaryColors } from '@tryhackme/thm-ui-components';
import styled, { keyframes } from 'styled-components';

export const StyledContainer = styled.div<{ $margins?: { mobile: string; desktop: string } }>`
  display: flex;
  flex-direction: column;
  gap: 5rem;
  margin: ${({ $margins }) => $margins?.mobile || '4.6rem 1.2rem 0'};
  align-items: center;

  @media screen and (min-width: ${({ theme }) => theme.sizes.xl}) {
    margin: ${({ $margins }) => $margins?.desktop || '11.2rem 0 0'};
  }
`;

export const StyledTitle = styled.h2`
  margin: 0;
  line-height: 4rem;
  font-size: ${({ theme }) => theme.fonts.sizes.subTitle};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
`;

export const StyledCategoryButton = styled.button<{ $active: boolean }>`
  height: 2.5rem;
  padding: 0 1.3rem;
  font-size: ${({ theme }) => theme.fonts.sizes.small};
  border: 1px solid ${secondaryColors.secondary.lighter};
  border-radius: 3.92rem;
  line-height: 2rem;
  transition: all 300ms cubic-bezier(0.87, 0, 0.13, 1);

  background-color: ${({ $active }) => ($active ? primaryColors.main : secondaryColors.secondary.main)};
  color: ${({ $active }) => ($active ? secondaryColors.black : secondaryColors.grey[400])};

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.medium};
    height: 3.92rem;
    padding: 0 2.1rem;
  }
`;

export const StyledAccordionWrapper = styled(Accordion.Root).attrs({
  type: 'single',
  collapsible: true,
})`
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;
`;

export const StyledAccordion = styled(Accordion.Item)`
  overflow: hidden;
  &:not(:last-child) {
    border-bottom: 1px solid ${secondaryColors.secondary.light};
  }
`;

const slideDown = keyframes`
  from {
		height: 0;
	}
	to {
		height: var(--radix-accordion-content-height);
	}
`;

const slideUp = keyframes`
	from {
		height: var(--radix-accordion-content-height);
	}
	to {
		height: 0;
	}
`;

export const StyledAccordionContent = styled(Accordion.Content)`
  background-color: ${secondaryColors.secondary.light};
  color: ${secondaryColors.grey[400]};
  white-space: pre-line;

  > p {
    padding: 2rem;
  }

  &[data-state='open'] {
    animation: ${slideDown} 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }

  &[data-state='closed'] {
    animation: ${slideUp} 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }
`;

export const StyledChevron = styled(FontAwesomeIcon).attrs({
  icon: faAngleDown,
})`
  font-size: ${({ theme }) => theme.fonts.sizes.small};
  line-height: 1.4rem;
  font-weight: 300;
  color: ${secondaryColors.grey[400]};
  transition: transform 300ms cubic-bezier(0.87, 0, 0.13, 1);
`;

export const StyledAccordionTrigger = styled(Accordion.Trigger)`
  width: 100%;
  height: 6rem;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.main};
  display: flex;
  justify-content: space-between;
  align-items: center;

  &[data-state='open'] {
    > ${StyledChevron} {
      transform: rotate(180deg);
    }
  }
`;

export const StyledQuestion = styled.p`
  text-align: left;
  line-height: ${({ theme }) => theme.fonts.sizes.default};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
`;
